import {
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isNotFoundError,
  isSuccess,
} from '@/context/services/type';
import { useCancelAdminInvoiceReport as useBaseCancelAdminInvoiceReport } from 'ap-openapi';
import { useCallback } from 'react';

/** Outcome variants returned by the admin invoice report cancel API call. */
export enum AdminInvoiceReportCancelStatus {
  SUCCESS = 'success',
  UNAUTHORIZED = 'unauthorized',
  FORBIDDEN = 'forbidden',
  NOT_FOUND = 'not_found',
  INVALID = 'invalid',
  ERROR = 'error',
}

/** Discriminated union of all possible outcomes from `useCancelAdminInvoiceReportService`. */
export type AdminInvoiceReportCancelResult =
  | { status: AdminInvoiceReportCancelStatus.SUCCESS }
  | { status: AdminInvoiceReportCancelStatus.UNAUTHORIZED }
  | {
      status: AdminInvoiceReportCancelStatus.FORBIDDEN;
      message: string | undefined;
      code: string | undefined;
    }
  | {
      status: AdminInvoiceReportCancelStatus.NOT_FOUND;
      message: string | undefined;
    }
  | {
      status: AdminInvoiceReportCancelStatus.INVALID;
      message: string | undefined;
    }
  | {
      status: AdminInvoiceReportCancelStatus.ERROR;
      message: string | undefined;
    };

const extractMessage = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined;
  if (!('messages' in data)) return undefined;
  const m = data['messages'];
  if (Array.isArray(m) && m.every((x): x is string => typeof x === 'string')) {
    return m.join('\n');
  }
  return undefined;
};

const extractCode = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined;
  if (!('code' in data) || typeof data['code'] !== 'string') return undefined;
  return data['code'];
};

export const useCancelAdminInvoiceReportService = () => {
  const { mutateAsync: _mutateAsync } = useBaseCancelAdminInvoiceReport();

  const mutateAsync = useCallback(
    async (id: string): Promise<AdminInvoiceReportCancelResult> => {
      try {
        const resp = await _mutateAsync({ id });
        if (isSuccess(resp.status)) {
          return { status: AdminInvoiceReportCancelStatus.SUCCESS };
        }
        const message = extractMessage(resp.data);
        const code = extractCode(resp.data);
        if (isAuthError(resp.status)) {
          return { status: AdminInvoiceReportCancelStatus.UNAUTHORIZED };
        }
        if (isForbiddenError(resp.status)) {
          return {
            status: AdminInvoiceReportCancelStatus.FORBIDDEN,
            message,
            code,
          };
        }
        if (isNotFoundError(resp.status)) {
          return { status: AdminInvoiceReportCancelStatus.NOT_FOUND, message };
        }
        if (isInvalidError(resp.status)) {
          return { status: AdminInvoiceReportCancelStatus.INVALID, message };
        }
        return { status: AdminInvoiceReportCancelStatus.ERROR, message };
      } catch {
        return {
          status: AdminInvoiceReportCancelStatus.ERROR,
          message: undefined,
        };
      }
    },
    [_mutateAsync]
  );

  return { mutateAsync };
};
